import {
    useContext,
    useState,
    type FC,
    memo
} from "react";
import {
    type LayoutChangeEvent
} from "react-native";
import type IDataTableColumnHeaderProps from "./type";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useDataTableContext
} from "../../context";
import {
    getDataTableIsShiftPressed,
    getDataTableAriaSort
} from "../../util";
import {
    getDataTableColumnProps
} from "../content/stylesheet";
import {
    TableContext
} from "../../../table/context";
import DataTableColumnResizer from "../../internal/columnResizer";
import DataTableSortButton from "../../internal/sortButton";
import Table from "../../../table";
import Text from "../../../text";

const DataTableColumnHeader: FC<IDataTableColumnHeaderProps> = ({
    stickyOffset,
    customTheme,
    isSticky,
    columnId
}) => {
    const context = useDataTableContext({
        part: "ColumnHeader"
    });

    const {
        controlHeights
    } = NCoreUIKitTheme.useContext(customTheme ?? context?.customTheme);

    const tableContext = useContext(TableContext);

    const [
        measuredWidth,
        setMeasuredWidth
    ] = useState(0);

    const column = context?.columns.find((item) => item.id === columnId);

    if(!context || !column) {
        return null;
    }

    const currentTheme = customTheme ?? context.customTheme;

    const rankIndex = context.sorting.findIndex((rule) => rule.id === column.id);

    const rule = context.sorting[rankIndex];

    const isSortable = !!column.isSortable && context.sortBehaviour !== "none";

    const isResizable = column.isResizable !== false && !column.isHeaderHidden && context.layout === "table";

    const currentWidth = context.columnWidths[column.id] ?? measuredWidth;

    const onLayout = (event: LayoutChangeEvent) => {
        setMeasuredWidth(Math.round(event.nativeEvent.layout.width));
    };

    const headProps = {
        ...getDataTableColumnProps({
            width: context.columnWidths[column.id],
            column
        }),
        sortDirection: rankIndex === 0 ? getDataTableAriaSort({
            direction: rule?.direction
        }) : undefined,
        stickyOffset,
        customTheme,
        onLayout,
        isSticky
    };

    const handlePress = (event: unknown) => {
        context.handleSort(column.id, getDataTableIsShiftPressed(event));
    };

    if(column.isHeaderHidden) {
        return <Table.Head
            {...headProps}
            aria-label={column.header}
        />;
    }

    if(!isSortable && !isResizable) {
        return <Table.Head
            {...headProps}
        >
            {column.header}
        </Table.Head>;
    }

    const renderResizer = () => {
        if(!isResizable) {
            return null;
        }

        return <DataTableColumnResizer
            isLast={context.columns[context.columns.length - 1]?.id === column.id}
            minWidth={column.minWidth ?? controlHeights.large * 2}
            customTheme={currentTheme}
            columnId={column.id}
            width={currentWidth}
        />;
    };

    const renderTitle = () => {
        if(isSortable) {
            return <DataTableSortButton
                rank={context.sorting.length > 1 && rankIndex >= 0 ? rankIndex + 1 : undefined}
                direction={rule?.direction}
                customTheme={currentTheme}
                onPress={handlePress}
                title={column.header}
            />;
        }

        return <Text
            variant={tableContext?.measures.headVariant ?? "labelMediumSize"}
            customTheme={currentTheme}
            numberOfLines={1}
            color="mid"
        >
            {column.header}
        </Text>;
    };

    return <Table.Head
        {...headProps}
    >
        {renderTitle()}
        {renderResizer()}
    </Table.Head>;
};
export default memo(DataTableColumnHeader);
